import { LinearTransform } from '@st/geom'

export type BoxConstraints = {
  minWidth: number
  maxWidth: number
  minHeight: number
  maxHeight: number
}

export const BoxConstraints = {
  /** Clamp the child's requested bounds to its parent's bounds. A conflicting
   * explicit child size must never loosen a tight parent constraint.
   */
  enforce: (parent: BoxConstraints, self: BoxConstraints): BoxConstraints => ({
    minWidth: Math.min(Math.max(self.minWidth, parent.minWidth), parent.maxWidth),
    maxWidth: Math.min(Math.max(self.maxWidth, parent.minWidth), parent.maxWidth),
    minHeight: Math.min(Math.max(self.minHeight, parent.minHeight), parent.maxHeight),
    maxHeight: Math.min(Math.max(self.maxHeight, parent.minHeight), parent.maxHeight)
  }),

  tight: (size: Size): BoxConstraints => {
    return {
      minWidth: size.width,
      maxWidth: size.width,
      minHeight: size.height,
      maxHeight: size.height
    }
  },

  /**
   * Returns a constraint with the minimum dimensions set to 0,
   * while preserving the original maximum dimensions.
   *
   * This is equivalent to Flutter's BoxConstraints.loosen() method.
   * It's used when a parent wants to let a child be as small as it wants.
   */
  loosen: (constraints: BoxConstraints): BoxConstraints => {
    return {
      minWidth: 0,
      maxWidth: constraints.maxWidth,
      minHeight: 0,
      maxHeight: constraints.maxHeight
    }
  },

  /**
   * Clamps a raw size so it satisfies this constraint rectangle.
   * Same semantics as Flutter's BoxConstraints.constrain().
   */
  constrain: (c: BoxConstraints, size: { width: number; height: number }) => ({
    width: Math.min(Math.max(size.width, c.minWidth), c.maxWidth),
    height: Math.min(Math.max(size.height, c.minHeight), c.maxHeight)
  }),

  /**
   * Returns a new constraint rectangle deflated by the given padding.
   * Same semantics as Flutter's BoxConstraints.deflate().
   */
  deflate: (c: BoxConstraints, p: EdgeInsets): BoxConstraints => ({
    minWidth: Math.max(0, c.minWidth - p.left - p.right),
    maxWidth: Math.max(0, c.maxWidth - p.left - p.right),
    minHeight: Math.max(0, c.minHeight - p.top - p.bottom),
    maxHeight: Math.max(0, c.maxHeight - p.top - p.bottom)
  })
}

export type Font = {
  family: string
  weight: 'normal' | 'bold'
  style?: 'normal' | 'italic'
  size: number
}

export type Size = {
  width: number
  height: number
}

export type Rect = {
  x: number
  y: number
  width: number
  height: number
}

export type Point = {
  x: number
  y: number
}

export type Border = {
  color: string
  width: number
  radius?: number
}

export type EdgeInsets = { left: number; top: number; right: number; bottom: number }

export type TextAlign = 'left' | 'center' | 'right'

export const Pad = {
  horizontal: (n: number) => ({ left: n, right: n, top: 0, bottom: 0 }),
  vertical: (n: number) => ({ left: 0, right: 0, top: n, bottom: n }),
  symmetric: (opts: { horizontal?: number; vertical?: number }): EdgeInsets => {
    return {
      left: opts.horizontal ?? 0,
      top: opts.vertical ?? 0,
      right: opts.horizontal ?? 0,
      bottom: opts.vertical ?? 0
    }
  }
}

type RenderedNode = {
  bounds: Rect
}

export type PaintOp = RenderedNode &
  (
    | {
        type: 'group'
        children: PaintOp[]
      }
    | {
        type: 'text'
        text: string
        font: Font
        color: string
        line_height: number
        line_through?: boolean
        text_align: TextAlign
        kerning: false
      }
    | { type: 'rect'; border?: Border; background_color?: string }
    | {
        type: 'path'
        path: string
        fill?: string
        stroke?: string
        stroke_width?: number
        transform?: LinearTransform
      }
  )

type RenderedMarkupAnnotation = RenderedNode & {
  id?: string
  author?: string
  timestamp?: string
}

export type RenderedAnnotation =
  | (RenderedNode & {
      type: 'link'
      destination: string
    })
  | (RenderedNode & {
      type: 'text_field'
      name: string
      value: string
      font_size?: number
      multiline: boolean
      border?: Border
      background_color?: string
      mask_char?: string
      onChange?: (value: string) => void
    })
  | (RenderedNode & {
      type: 'checkbox'
      name: string
      checked: boolean
      border?: Border
      background_color?: string
      onChange?: (checked: boolean) => void
    })
  | (RenderedNode & {
      type: 'radio'
      name: string
      value: string
      export_value: string
      selected: boolean
      background_color?: string
      onChange?: (selected: boolean) => void
    })
  | (RenderedNode & {
      type: 'select'
      name: string
      value?: string
      options: Array<{ value: string; label: string }>
      border?: Border
      background_color?: string
      onChange?: (value: string) => void
    })
  | (RenderedMarkupAnnotation & {
      type: 'comment'
      content: string
      color: string
    })
  | (RenderedMarkupAnnotation & {
      type: 'rectangle'
      border: Border
      appearance: PaintOp
    })
  | (RenderedMarkupAnnotation & {
      type: 'text'
      content: string
      color: string
      font: Font
      appearance: PaintOp
    })
  | (RenderedMarkupAnnotation & {
      type: 'stamp'
      appearance: PaintOp
    })

export type RenderedPage = {
  /** Visual paint, independent of interaction and native document semantics. */
  content: PaintOp
  /** Page-absolute semantics emitted from the same laid-out tree as `content`. */
  annotations: RenderedAnnotation[]
  bookmarks: RenderedBookmark[]
}

export type RenderedBookmark = {
  title: string
  path?: string
  bounds: Rect
}

export abstract class RenderBox<Prop = any> {
  children: RenderBox[] = []
  box: Rect = { x: 0, y: 0, width: 0, height: 0 }

  constructor(public props: Prop) {}
  add(child: RenderBox) {
    this.children.push(child)
  }

  abstract performLayout(c: BoxConstraints): void

  draw(): PaintOp {
    return {
      type: 'group',
      bounds: this.box,
      children: this.children.map((c) => c.draw())
    }
  }

  annotation(): RenderedAnnotation | undefined {
    return undefined
  }

  bookmark(): RenderedBookmark | undefined {
    return undefined
  }

  removeChild(child: RenderBox) {
    const i = this.children.indexOf(child)
    if (i !== -1) this.children.splice(i, 1)
  }
}

export class ViewportRenderBox extends RenderBox<{ width: number; height: number }> {
  performLayout(): void {
    this.box = { x: 0, y: 0, width: this.props.width, height: this.props.height }

    const tight = BoxConstraints.tight({
      width: this.props.width,
      height: this.props.height
    })

    for (const child of this.children) {
      child.performLayout(tight)
      child.box.x = 0
      child.box.y = 0
    }
  }
}

/**
 * Resolves page semantics from the same laid-out RenderBox tree that produces
 * paint. Bounds become page-absolute exactly once here; no surface or backend
 * repeats origin accumulation.
 */
export function renderPage(root: ViewportRenderBox): RenderedPage {
  root.performLayout()
  return paintPage(root)
}

/** Collect an already-laid-out page without changing its child constraints. */
export function paintPage(root: RenderBox): RenderedPage {
  const annotations: RenderedAnnotation[] = []
  const bookmarks: RenderedBookmark[] = []

  walkRenderBoxes(root, { x: 0, y: 0 }, (box, origin) => {
    const annotation = box.annotation()
    const bookmark = box.bookmark()
    const bounds = {
      x: origin.x,
      y: origin.y,
      width: box.box.width,
      height: box.box.height
    }

    if (annotation) annotations.push({ ...annotation, bounds })
    if (bookmark) bookmarks.push({ ...bookmark, bounds })
  })

  return { content: root.draw(), annotations, bookmarks }
}

function walkRenderBoxes(
  box: RenderBox,
  parentOrigin: Point,
  visitor: (box: RenderBox, origin: Point) => void
) {
  const origin = {
    x: parentOrigin.x + box.box.x,
    y: parentOrigin.y + box.box.y
  }

  visitor(box, origin)
  for (const child of box.children) walkRenderBoxes(child, origin, visitor)
}
